import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import {
  DEFAULT_DISTRICT_BRANDING_COLORS,
  DistrictBrandingPayload,
  DistrictDTO,
} from '../../../common/dtos/district.dto';
import { EnvironmentService } from '../../../common/services/environment/environment.service';
import { User } from '../../../common/state/user/user.model';
import { UserState } from '../../../common/state/user/user.state';
import { getContrastRatio } from '../../../common/utilities/contrast-checker';
import { UploadStyles } from '../../shared/components/file-management/file-upload/file-upload.component';
import { FileDTO } from '../../shared/dtos/file.dto';
import { FileType } from '../../shared/enums/file-type.enum';
import { AlertService } from '../../shared/services/alert/alert.service';
import { DistrictBrandingService } from '../../shared/services/district-branding/district-branding.service';
import { DistrictListService } from '../../shared/services/district-list/district-list-service';

@Component({
  selector: 'app-district-branding-page',
  standalone: false,
  templateUrl: './district-branding-page.component.html',
  styleUrl: './district-branding-page.component.scss',
})
export class DistrictBrandingPageComponent implements OnDestroy {
  @ViewChild('backgroundColorInput')
  backgroundColorInput: ElementRef<HTMLInputElement>;

  @ViewChild('linkColorInput')
  linkColorInput: ElementRef<HTMLInputElement>;

  @ViewChild('activeBackgroundColorInput')
  activeBackgroundColorInput: ElementRef<HTMLInputElement>;

  @ViewChild('activeLinkColorInput')
  activeLinkColorInput: ElementRef<HTMLInputElement>;

  districtCode: string;

  districtId: number;

  currentUserDistrictId: number | null = null;

  districtName: string;

  payload: DistrictBrandingPayload;

  uploadStyle = UploadStyles.SIMPLE;

  allowedFileTypes = FileType.IMAGE;

  logoPath: string;

  hasExistingBrandingRecord = false;

  isLoading = true;

  previewItems = [
    { key: 'dashboard', label: 'Dashboard', icon: 'grid-1x2' },
    { key: 'calendar', label: 'Calendar', icon: 'calendar2-date' },
    { key: 'portfolio', label: 'Portfolio', icon: 'briefcase' },
    { key: 'resources', label: 'Resources', icon: 'book' },
  ];

  previewActiveNav = 'dashboard';

  submitLoading = false;

  saveBrandingError = '';

  readonly wcagThresholds = {
    normalTextAA: 4.5,
    normalTextAAA: 7,
    uiComponentsAA: 3,
  };

  subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private districtBrandingService: DistrictBrandingService,
    private districtListService: DistrictListService,
    private alertService: AlertService
  ) {
    this.districtCode = this.route.snapshot.paramMap.get(
      'districtCode'
    ) as string;

    const user = this.store.selectSnapshot(UserState.getUser) as User;
    this.currentUserDistrictId = user?.district?.id ?? null;

    this.subscriptions.add(
      this.districtListService
        .fetchDistrictsSimple({ per_page: 1000 })
        .subscribe((districts) => {
          if (districts) {
            const district = districts.find(
              (searchDistrict) =>
                searchDistrict.districtCode === this.districtCode
            ) as DistrictDTO;
            if (district) {
              this.districtId = district.id;
              this.districtName = district.title;
              this.getDistrictBranding();
            }
          }
        })
    );
  }

  getDistrictBranding() {
    this.subscriptions.add(
      this.districtBrandingService
        .getDistrictBranding(this.districtId)
        .subscribe({
          next: (response) => {
            if (response) {
              this.hasExistingBrandingRecord = true;
              if (response.item.logo) {
                this.logoPath =
                  EnvironmentService.apiUrl() + response.item.logo;
              } else {
                this.logoPath = '';
              }
              this.payload = {
                district_id: response.item.district_id,
                logo: response.item.logo,
                bg_color: response.item.bg_color,
                active_bg_color: response.item.active_bg_color,
                link_color: response.item.link_color,
                active_link_color: response.item.active_link_color,
              };
              if (this.districtId === this.currentUserDistrictId) {
                this.districtBrandingService.setCurrentBranding(this.payload);
              }
              this.isLoading = false;
            } else {
              this.setDefaultPayload();
            }
          },
          error: (error) => {
            if (error && error.status === 404) {
              this.setDefaultPayload();
            } else {
              throw error;
            }
          },
        })
    );
  }

  private setDefaultPayload() {
    this.payload = {
      district_id: this.districtId || 0,
      logo: '',
      ...DEFAULT_DISTRICT_BRANDING_COLORS,
    };
    this.logoPath = '';
    if (this.districtId === this.currentUserDistrictId) {
      this.districtBrandingService.setCurrentBranding(this.payload);
    }
    this.isLoading = false;
  }

  setPreviewActive(key: string) {
    this.previewActiveNav = key;
  }

  openColorPicker(colorType: string) {
    switch (colorType) {
      case 'background':
        this.backgroundColorInput.nativeElement.click();
        break;
      case 'active_background':
        this.activeBackgroundColorInput.nativeElement.click();
        break;
      case 'link':
        this.linkColorInput.nativeElement.click();
        break;
      case 'active_link':
        this.activeLinkColorInput.nativeElement.click();
        break;
      default:
        break;
    }
  }

  updateColor(event: Event, colorType: string) {
    const input = event.target as HTMLInputElement;
    switch (colorType) {
      case 'background':
        this.payload.bg_color = input.value;
        break;
      case 'active_background':
        this.payload.active_bg_color = input.value;
        break;
      case 'link':
        this.payload.link_color = input.value;
        break;
      case 'active_link':
        this.payload.active_link_color = input.value;
        break;
      default:
        break;
    }
  }

  get defaultContrastRatio(): number {
    if (!this.payload) {
      return 1;
    }

    return getContrastRatio(this.payload.link_color, this.payload.bg_color);
  }

  get activeContrastRatio(): number {
    if (!this.payload) {
      return 1;
    }

    return getContrastRatio(
      this.payload.active_link_color,
      this.payload.active_bg_color
    );
  }

  // eslint-disable-next-line class-methods-use-this
  isPassingContrast(ratio: number, minimumRatio: number): boolean {
    return ratio >= minimumRatio;
  }

  deleteAttachment() {
    this.payload.logo = '';
    this.logoPath = '';
  }

  // Handles updating of file list on frontend, doesn't make any API calls
  updateAttachments(file: FileDTO) {
    this.payload.logo = file.location;
    this.logoPath = EnvironmentService.apiUrl() + file.location;
  }

  resetChanges() {
    this.saveBrandingError = '';
    this.getDistrictBranding();
  }

  resetToDefaultColors() {
    this.payload = {
      ...this.payload,
      ...DEFAULT_DISTRICT_BRANDING_COLORS,
    };
  }

  saveBranding() {
    this.submitLoading = true;
    this.saveBrandingError = '';
    if (this.hasExistingBrandingRecord) {
      this.subscriptions.add(
        this.districtBrandingService
          .updateDistrictBranding(this.districtId, this.payload)
          .subscribe({
            next: () => {
              this.submitLoading = false;
              if (this.districtId === this.currentUserDistrictId) {
                this.districtBrandingService.setCurrentBranding(this.payload);
              }
              this.alertService.showAlert(
                `District Branding Updated for ${this.districtName}`
              );
            },
            error: (err) => {
              this.submitLoading = false;
              this.saveBrandingError = err.error.message;
            },
          })
      );
    } else {
      this.subscriptions.add(
        this.districtBrandingService
          .createDistrictBranding(this.payload)
          .subscribe({
            next: () => {
              this.submitLoading = false;
              if (this.districtId === this.currentUserDistrictId) {
                this.districtBrandingService.setCurrentBranding(this.payload);
              }
              this.alertService.showAlert(
                `District Branding Updated for ${this.districtName}`
              );
            },
            error: (err) => {
              this.submitLoading = false;
              this.saveBrandingError = err.error.message;
            },
          })
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
